import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditInvoice = ({ invoice, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    status: invoice.status || 'draft',
    invoice_date: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : '',
    due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
    client_id: invoice.client_id || '',
    notes: invoice.notes || '',
    amount: invoice.amount || 0,
    gst_rate: invoice.gst_rate || 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${API}/clients`);
        setClients(response.data);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (formData.invoice_date) {
      setFormData(prev => ({ ...prev, due_date: prev.invoice_date }));
    }
  }, [formData.invoice_date]);

  const calculateTotals = () => {
    const amount = parseFloat(formData.amount) || 0;
    const gstRate = parseFloat(formData.gst_rate) || 0;
    const gstAmount = amount * (gstRate / 100);
    const totalAmount = amount + gstAmount;
    return { amount, gstAmount, totalAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { gstAmount, totalAmount } = calculateTotals();
      
      const updateData = {
        ...formData,
        amount: parseFloat(formData.amount),
        gst_rate: parseFloat(formData.gst_rate),
        gst_amount: gstAmount,
        total_amount: totalAmount,
        due_date: formData.invoice_date
      };

      await axios.put(`${API}/invoices/${invoice.id}`, updateData);
      alert('Invoice updated successfully!');
      onSave();
    } catch (error) {
      console.error('Error updating invoice:', error);
      alert('Error updating invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  const { gstAmount, totalAmount } = calculateTotals();

  return (
    <div className="p-6">
      <DialogHeader className="mb-6">
        <DialogTitle className="text-2xl font-bold text-gray-800 flex items-center">
          <Edit className="w-6 h-6 mr-3 text-blue-600" />
          Edit Invoice #{invoice.invoice_number}
        </DialogTitle>
        <DialogDescription className="text-gray-600">
          Update invoice details and recalculate totals
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="client_id">Client *</Label>
            <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} - {client.company || 'No Company'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="status">Invoice Status *</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="invoice_date">Invoice Date *</Label>
            <Input
              id="invoice_date"
              type="date"
              value={formData.invoice_date}
              onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="due_date">Due Date (Auto-set = Invoice Date)</Label>
            <Input
              id="due_date"
              type="date"
              value={formData.due_date}
              disabled
              className="bg-gray-100 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Due date automatically matches invoice date</p>
          </div>

          <div>
            <Label htmlFor="amount">Amount *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({...formData, amount: e.target.value})}
              required
            />
          </div>

          <div>
            <Label htmlFor="gst_rate">GST Rate (%) *</Label>
            <Input
              id="gst_rate"
              type="number"
              step="0.01"
              value={formData.gst_rate}
              onChange={(e) => setFormData({...formData, gst_rate: e.target.value})}
              required
            />
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Notes</Label>
          <textarea
            id="notes"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="3"
            placeholder="Add any additional notes..."
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
          />
        </div>

        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg text-blue-800">Updated Calculation</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-gray-700">
              <span>Base Amount:</span>
              <span>{parseFloat(formData.amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-700">
              <span>GST ({formData.gst_rate}%):</span>
              <span>{gstAmount.toFixed(2)}</span>
            </div>
            <hr className="border-blue-200" />
            <div className="flex justify-between text-lg font-bold text-blue-800">
              <span>Total Amount:</span>
              <span>{totalAmount.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {isSubmitting ? 'Updating...' : 'Update Invoice'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditInvoice;
